import VerticalDragAndDrop from "@/components-legacy/verticalDragAndDrop";
import { useBoolean } from "@/hooks/use-boolean";
import { getReasonTextFromIdAndLang } from "@/lib/reasons/reason-text-from-id-and-lang";
import {
  ShopDto,
  ShopLocationDto,
  ShopRoutingLocationRuleDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import SimpleMenu from "@babackapp/ui/components/simple-menu";
import { TrashIcon } from "@heroicons/react/24/outline";
import { MoreHorizontal, Pencil } from "lucide-react";
import { useState } from "react";
import { useCarriersContext } from "../../use-carriers-context";
import { AddRoutingLocationRulesDialog } from "../forms/add-routing-location-rules-dialog";
import { EditRoutingLocationRulesDialog } from "../forms/edit-one-carrier-rule-dialog";
import { RemoveOneRuleDialog } from "../forms/remove-one-rule-dialog";

export default function RoutingRule(props: { shop: ShopDto }) {
  const t = useTranslation();
  const { editOneCarrierRutingRule } = useCarriersContext();
  const openDeleteCarrer = useBoolean(false);
  const openEditCarrer = useBoolean(false);

  const [selectedRule, setSelectedRule] =
    useState<ShopRoutingLocationRuleDto | null>(null);

  const onClickDeleteOneRule = (rule: ShopRoutingLocationRuleDto) => {
    setSelectedRule(rule);
    openDeleteCarrer.setTrue();
  };

  const onClickEditOneRule = (rule: ShopRoutingLocationRuleDto) => {
    setSelectedRule(rule);
    openEditCarrer.setTrue();
  };

  return (
    <div className="pt-2">
      {selectedRule && openDeleteCarrer.value && (
        <RemoveOneRuleDialog
          isOpen={openDeleteCarrer.value}
          setIsOpen={openDeleteCarrer.setValue}
          selectedRule={selectedRule}
          onCloseForm={openDeleteCarrer.setFalse}
        />
      )}

      {selectedRule && openEditCarrer.value && (
        <EditRoutingLocationRulesDialog
          isOpen={openEditCarrer.value}
          setIsOpen={openEditCarrer.setValue}
          selectedRule={selectedRule}
          onCloseForm={openEditCarrer.setFalse}
        />
      )}
      <div className="">{t("settings.shipping.rules.title")}</div>
      <VerticalDragAndDrop<
        ShopRoutingLocationRuleDto & {
          id: string;
        }
      >
        items={props.shop.routingLocationRules as any}
        onChange={editOneCarrierRutingRule}
        itemRenderer={(rule: any) => {
          const location = props.shop.locations.find(
            (loc: any) => loc.id === rule.positiveResult,
          );
          return (
            <div className="border-gray-200 border rounded-md my-2 py-4 px-6 bg-white text-sm flex">
              <div className="flex-1">
                <div className="text-gray-900">
                  {rule.key === "reasonId"
                    ? t("settings.shipping.rules.ifthereason", {
                        reason: getReasonTextFromIdAndLang(
                          rule.value,
                          props.shop.preferences.languages[0],
                          props.shop,
                        ),
                        interpolation: { escapeValue: false },
                      })
                    : rule.key === "orderTag"
                      ? t("settings.shipping.rules.ifOrderTag", {
                          tag: rule.value,
                        })
                      : t("settings.shipping.rules.ifCountryCode", {
                          code: rule.value,
                        })}
                </div>
                <div className="text-gray-500 font-light">
                  {t("settings.shipping.rules.sendit", {
                    location: location?.address?.companyName,
                  })}
                </div>
              </div>
              <div className="">
                <SimpleMenu
                  iconTitle={<MoreHorizontal className="h-4 w-4" />}
                  position="left"
                  type={"neutral"}
                  menu={[
                    {
                      onClick: () => {
                        onClickEditOneRule(rule);
                      },
                      title: t("settings.integrations.webhooks.menu.edit"),
                      icon: <Pencil className="h-4 w-4 mr-1" />,
                    },
                    {
                      onClick: () => {
                        onClickDeleteOneRule(rule);
                      },
                      renderTitle: () => (
                        <p className="text-red-500">{t("misc.delete")}</p>
                      ),
                      icon: <TrashIcon className="h-4 w-4 mr-1 text-red-500" />,
                    },
                  ]}
                />
              </div>
            </div>
          );
        }}
      />

      <div className="border-gray-200 border rounded-md my-2 py-4 px-6 bg-white text-sm ">
        <div className="text-gray-900">
          {t("settings.shipping.rules.default")}
        </div>
        <div className="text-gray-500 font-light">
          {t("settings.shipping.rules.sendit", {
            location: props.shop.locations?.find(
              (location: ShopLocationDto) => location.isDefault,
            )?.address?.companyName,
          })}
        </div>
      </div>
      <AddRoutingLocationRulesDialog>
        <Button variant="secondary">
          {t("settings.shipping.rules.addRule")}
        </Button>
      </AddRoutingLocationRulesDialog>
    </div>
  );
}
